import { useEffect, type ReactNode } from 'react';
import { cn } from '../cn';
import { Button } from './Button';

export interface DialogProps {
  open: boolean;
  title: string;
  children?: ReactNode;
  onClose: () => void;
  /** Confirm button; `destructive` renders the red outline. */
  confirm?: { label: string; onClick: () => void | Promise<void>; destructive?: boolean; disabled?: boolean; busy?: boolean };
  cancelLabel?: string;
  /** Mono meta under the title. */
  meta?: string;
  className?: string;
  /** Frosted sheet over a blurred page (the Groups editor): translucent panel, heavier blur, wider. */
  glass?: boolean;
  size?: 'md' | 'lg' | 'xl';
}

/** Confirm dialog (delete / wipe / downgrade): panel surface, radius 12, 20 px padding, max 420 px. */
export function Dialog({ open, title, children, onClose, confirm, cancelLabel = 'Cancel', meta, className, glass, size = 'md' }: DialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className={cn('fixed inset-0 z-50 flex items-end justify-center p-4 md:items-center', glass ? 'bg-black/35 backdrop-blur-md' : 'bg-black/55')} onClick={onClose} role="presentation">
      <div
        role="dialog"
        aria-modal
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        style={glass ? { background: 'color-mix(in srgb, var(--panel) 78%, transparent)' } : undefined}
        className={cn(
          // every size fits the window: title and buttons stay put, only the body scrolls
          'flex max-h-[calc(100dvh-2rem)] w-full flex-col gap-4 rounded-2xl border border-line p-5 shadow-[0_24px_60px_rgba(0,0,0,.45)]',
          size === 'md' && 'max-w-[420px]',
          size === 'lg' && 'max-w-[640px]',
          size === 'xl' && 'max-w-[920px]',
          glass ? 'border-white/15 backdrop-blur-2xl backdrop-saturate-150' : 'bg-panel',
          className,
        )}
      >
        <div className="flex flex-col gap-1.5">
          <span className="text-xl font-medium leading-tight">{title}</span>
          {meta && <span className="meta text-fg4">{meta}</span>}
        </div>
        {children && <div className="-mx-1 min-h-0 overflow-y-auto px-1 text-pretty text-base font-normal leading-[1.6] text-fg2">{children}</div>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" size="responsive" onClick={onClose}>
            {cancelLabel}
          </Button>
          {confirm && (
            <Button variant={confirm.destructive ? 'destructive-outline' : 'primary'} size="responsive" disabled={confirm.disabled || confirm.busy} onClick={() => void confirm.onClick()}>
              {confirm.busy ? '…' : confirm.label}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Bottom sheet (mobile PIN / confirmations). Same surface, anchored to the bottom, 430 px max. */
export function Sheet({ open, title, children, onClose, className }: { open: boolean; title?: string; children: ReactNode; onClose?: () => void; className?: string }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/55" onClick={onClose} role="presentation">
      <div role="dialog" aria-modal aria-label={title} onClick={(e) => e.stopPropagation()} className={cn('flex w-full max-w-mobile flex-col gap-4 rounded-t-2xl border border-line bg-panel p-5 pb-8', className)}>
        {title && <span className="text-xl font-medium leading-tight">{title}</span>}
        {children}
      </div>
    </div>
  );
}
